import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Box, ButtonGroup, TextButton, Button, Icon } from "@zillow/constellation-10";
import { IconCloseOutline, IconMenuOutline } from "@zillow/constellation-icons";
import { yieldCallback } from "@zillow/yield-callback";
import { useEffect } from "react";
import { B2B_CONTENT_MAX_WIDTH } from "../../constants/layout.esm.js";
import { B2B_MAIN_NAV_LOGO_MAX_HEIGHT_PX } from "../../constants/stickyNav.esm.js";
import { getCmsInfoForPage } from "../../utils/clickstream/getCmsInfoForPage.esm.js";
import { sendEvent, getMobileNavMode } from "./B2BMainNav.utils.esm.js";
import MenuItem from "./MenuItem.esm.js";
import MenuList from "./MenuList.esm.js";
import { usePageProps } from "@zillow/cms-utils";
const B2BMainNav = (props) => {
  const {
    menu,
    mobileMenu,
    setMobileMenu,
    primaryCTALink,
    primaryCTALabel,
    secondaryCTALink,
    secondaryCTALabel,
    logo,
    disabled,
    transparent,
    secondaryCTAModal,
    sticky,
    mobileMenuTopPx
  } = props;
  function parseMenu(items, root = 0) {
    if (!items || !Array.isArray(items)) {
      return [];
    }
    return items.filter((item) => item.parent == root).map((menuItem) => {
      var _a, _b;
      return {
        id: menuItem.id,
        label: ((_a = menuItem == null ? void 0 : menuItem.title) == null ? void 0 : _a.raw) || ((_b = menuItem == null ? void 0 : menuItem.title) == null ? void 0 : _b.rendered),
        current: false,
        url: menuItem.url,
        target: menuItem.target,
        children: parseMenu(items.filter((child) => {
          var _a2;
          return ((_a2 = child == null ? void 0 : child.parent) == null ? void 0 : _a2.toString()) == menuItem.id;
        }), menuItem.id),
        ...menuItem.columns && {
          columns: menuItem.columns
        }
      };
    });
  }
  const menuItems = parseMenu(menu);
  const pageProps = usePageProps();
  const cmsInfo = getCmsInfoForPage(pageProps);
  const handleCTAClick = (triggerSource, label, url) => {
    sendEvent(triggerSource, label, url, cmsInfo);
  };
  const LogoContainer = ({
    children,
    ...rest
  }) => {
    if ((logo == null ? void 0 : logo.link) && !disabled) {
      return /* @__PURE__ */ jsx("a", { ...rest, children });
    }
    return /* @__PURE__ */ jsx(Fragment, { children });
  };
  const hasPrimaryCta = primaryCTALink && primaryCTALabel;
  const hasSecondaryCta = secondaryCTALink && secondaryCTALabel || secondaryCTAModal;
  const primaryButtonEmphasis = transparent ? "outlined" : "filled";
  const primaryOnImpact = transparent;
  useEffect(() => {
    if (mobileMenu) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenu]);
  const mobileNavMode = getMobileNavMode({
    sticky,
    mobileMenu
  });
  const mobileMenuTopStyle = mobileMenu && mobileMenuTopPx !== void 0 ? {
    "--b2b-mobile-menu-top": `${mobileMenuTopPx}px`
  } : void 0;
  return /* @__PURE__ */ jsx(Box, { role: "navigation", "aria-label": "Main Navigation", style: mobileMenuTopStyle, css: {
    zIndex: "menu",
    position: sticky ? "fixed" : "relative",
    ...sticky && {
      width: "100%"
    },
    top: 0,
    // The nav only pins once it has scrolled out of view, so slide
    // it down from above the viewport rather than popping it in.
    // `b2bMainNavSlideIn` is defined by B2BLayout's sticky <style>.
    ...sticky && {
      xl: {
        animation: "b2bMainNavSlideIn 250ms cubic-bezier(0.2, 0, 0, 1)",
        _motionReduce: {
          animation: "none"
        }
      }
    },
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: transparent ? "border.onImpact.neutral-fixed" : "border.muted",
    background: transparent ? "rgba(0, 0, 0, 0)" : "bg.neutral",
    xlDown: {
      // Literal values so Panda can extract them; the mode
      // (open menu wins over sticky) comes from getMobileNavMode.
      position: mobileNavMode === "menu" ? "absolute" : mobileNavMode === "sticky" ? "sticky" : "relative",
      // B2BLayout sets the variable when the menu opens so the
      // menu never starts above the top of the viewport.
      top: mobileNavMode === "menu" ? "var(--b2b-mobile-menu-top, 41px)" : "0",
      left: 0,
      right: 0,
      ...mobileMenu && {
        bottom: 0,
        height: "100dvh",
        minHeight: "100dvh"
      }
    }
  }, children: /* @__PURE__ */ jsxs(Box, { role: "navigation", "aria-label": "Main Navigation", style: {
    width: "100%",
    maxWidth: B2B_CONTENT_MAX_WIDTH,
    margin: "0 auto"
  }, css: {
    position: "relative",
    display: "flex",
    gap: "default",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "default",
    ...mobileMenu && {
      background: "bg.neutral"
    },
    xlDown: {
      alignItems: "start",
      flexDirection: "column",
      gap: "0",
      padding: "none",
      justifyContent: "flex-start",
      ...mobileMenu && {
        height: "100dvh",
        overflowY: "auto",
        overflowX: "hidden",
        paddingBottom: "layout.looser"
      }
    }
  }, children: [
    logo && /* @__PURE__ */ jsxs(Box, { css: {
      xl: {
        flex: "0 0 auto"
      },
      xlDown: {
        display: "flex",
        alignItems: "center",
        justifyContent: "unset",
        width: "100%",
        padding: "default"
      },
      lgDown: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%"
      }
    }, children: [
      /* @__PURE__ */ jsx(Box, { css: {
        xlDown: {
          flex: 1
        }
      }, children: /* @__PURE__ */ jsx(LogoContainer, { href: logo.link, style: {
        display: "block"
      }, children: /* @__PURE__ */ jsx("img", { src: logo.src, alt: logo.alt || "Logo", width: logo.width, height: logo.height, style: {
        display: "block",
        width: logo.width,
        maxWidth: "100%",
        height: "auto",
        maxHeight: B2B_MAIN_NAV_LOGO_MAX_HEIGHT_PX,
        flexShrink: 0,
        filter: transparent ? "saturate(0%) invert(100%) brightness(300%) contrast(100%)" : "none"
      } }) }) }),
      !disabled && /* @__PURE__ */ jsx(Box, { css: {
        display: "none",
        xlDown: {
          display: "block",
          marginRight: "default"
        },
        lgDown: {
          display: "none"
        }
      }, children: /* @__PURE__ */ jsxs(ButtonGroup, { "aria-label": "Calls to Action", children: [
        hasSecondaryCta && (secondaryCTAModal ? secondaryCTAModal : /* @__PURE__ */ jsx(TextButton, { asChild: true, tone: "brand", onImpact: transparent, css: {
          ":hover, :focus": {
            textDecoration: transparent ? "underline !important" : "none !important",
            color: transparent ? "text.onImpact.neutral-fixed" : "text.accent.brand.hero"
          },
          ":not(:hover)": {
            textDecoration: "none !important",
            color: transparent ? "text.onImpact.neutral-fixed" : "text.accent.brand.hero"
          }
        }, children: /* @__PURE__ */ jsx("a", { href: secondaryCTALink, onClick: yieldCallback(() => {
          handleCTAClick("cms_button", secondaryCTALabel, secondaryCTALink);
        }), children: secondaryCTALabel }) })),
        primaryCTALabel && primaryCTALink && /* @__PURE__ */ jsx(Button, { asChild: true, emphasis: primaryButtonEmphasis, tone: "brand", onImpact: primaryOnImpact, children: /* @__PURE__ */ jsx("a", { href: primaryCTALink, onClick: yieldCallback(() => {
          handleCTAClick("cms_button", primaryCTALabel, primaryCTALink);
        }), children: primaryCTALabel }) })
      ] }) }),
      !disabled && /* @__PURE__ */ jsx(Box, { css: {
        display: "none",
        alignSelf: "center",
        flexShrink: 0,
        xlDown: {
          display: "flex",
          alignItems: "center"
        }
      }, children: /* @__PURE__ */ jsx("button", { type: "button", "aria-expanded": mobileMenu, "aria-label": "Mobile Menu", onClick: () => setMobileMenu((prev) => !prev), style: {
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        display: "flex",
        alignItems: "center"
      }, children: /* @__PURE__ */ jsx(Icon, { size: "sm", css: {
        color: transparent ? "text.onImpact.neutral-fixed" : "text.accent.brand.hero"
      }, children: mobileMenu ? /* @__PURE__ */ jsx(IconCloseOutline, {}) : /* @__PURE__ */ jsx(IconMenuOutline, {}) }) }) })
    ] }),
    menu && !disabled && /* @__PURE__ */ jsx(MenuList, { css: {
      display: "flex",
      flex: "1 1 0%",
      minWidth: "0",
      height: "100%",
      alignItems: "center",
      xlDown: {
        display: mobileMenu ? "flex" : "none",
        flexDirection: "column",
        alignItems: "start",
        flex: "none",
        height: "auto",
        width: "100%",
        minWidth: "auto",
        borderTopWidth: "1px",
        borderTopStyle: "solid",
        borderTopColor: "border.muted"
      }
    }, children: menuItems.map((item) => /* @__PURE__ */ jsx(MenuItem, { item, transparent, css: {
      display: "flex",
      alignItems: "end"
    } }, item.id)) }),
    (hasPrimaryCta || hasSecondaryCta) && !disabled && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Box, { css: {
        xl: {
          flexShrink: 0
        },
        xlDown: {
          display: "none"
        }
      }, children: /* @__PURE__ */ jsxs(ButtonGroup, { "aria-label": "Call to Action buttons", children: [
        hasSecondaryCta && (secondaryCTAModal ? secondaryCTAModal : /* @__PURE__ */ jsx(TextButton, { asChild: true, tone: "brand", onImpact: transparent, css: {
          ":hover, :focus": {
            textDecoration: transparent ? "underline !important" : "none !important"
          },
          ":not(:hover)": {
            textDecoration: "none !important",
            color: transparent ? "text.onImpact.neutral-fixed" : "text.accent.brand.hero"
          }
        }, children: /* @__PURE__ */ jsx("a", { href: secondaryCTALink, onClick: yieldCallback(() => {
          handleCTAClick("cms_button", secondaryCTALabel, secondaryCTALink);
        }), children: secondaryCTALabel }) })),
        hasPrimaryCta && /* @__PURE__ */ jsx(Button, { asChild: true, emphasis: primaryButtonEmphasis, tone: "brand", onImpact: primaryOnImpact, children: /* @__PURE__ */ jsx("a", { href: primaryCTALink, onClick: yieldCallback(() => {
          handleCTAClick("cms_button", primaryCTALabel, primaryCTALink);
        }), children: primaryCTALabel }) })
      ] }) }),
      /* @__PURE__ */ jsx(Box, { css: {
        display: "none",
        lgDown: {
          display: mobileMenu ? "block" : "none",
          padding: "default",
          marginTop: "default",
          width: "100%"
        }
      }, children: /* @__PURE__ */ jsxs(ButtonGroup, { fluid: true, "aria-label": "Call to Action buttons", orientation: "vertical", children: [
        hasPrimaryCta && /* @__PURE__ */ jsx(Button, { asChild: true, emphasis: "filled", tone: "brand", children: /* @__PURE__ */ jsx("a", { href: primaryCTALink, onClick: yieldCallback(() => {
          handleCTAClick("cms_button", primaryCTALabel, primaryCTALink);
        }), children: primaryCTALabel }) }),
        hasSecondaryCta && (secondaryCTAModal ? secondaryCTAModal : /* @__PURE__ */ jsx(Button, { asChild: true, emphasis: "outlined", tone: "brand", children: /* @__PURE__ */ jsx("a", { href: secondaryCTALink, onClick: yieldCallback(() => {
          handleCTAClick("cms_button", secondaryCTALabel, secondaryCTALink);
        }), children: secondaryCTALabel }) }))
      ] }) })
    ] })
  ] }) });
};
export {
  B2BMainNav as default
};
